import { ProfileView, SearchOrder } from "@/types";

/**
 * START SEARCH
 * This file is the starting point for setting up search parameters. Since search is pretty complex and a lot
 * this comment kicks off a step-by-step guide to setting up a search query.
 * STEPS of Search (on the frontend):
 * 1. Ensure that schema.yml is up-to-date with the search parameters.
 * 2. Add the new search parameter to the correct endpoint interface.
 *    - This ensures type safety when handling parameters in the Route via TypedURLSearchParams.
 * 3. Add the new search parameter to the SearchQuery object.
 *     - This ensures type safety when handling parameters in the frontend via SearchQuery.
 * 4. Create the proper component to handle the new search parameter in the proper [Name]Search.vue component.
 *     - Double check to see if the component needs to only pass in the value as a string (or an object),
 *       passing as a string greatly simplifies the code needed to extract the value from the route.
 *       - Rules for deciding:
 *         - If the value passed to the api would make sense to the user, it should be passed as a string. (for example, a focus area)
 *         - If the value does not make sense it should be passed as an object. (for example, an "id" for a state)
 *         - This is important here because of how the SearchResultsChips component works.
 * 5. Edit the "ReadSearchQueriesFromRoute" function in SearchService.ts to handle the new search parameter.
 *      - If the new parameter is a string, you just need to do params.get(key) or params.getAll(key) and then let the
 *        frontend components handle the rest, as they can handle value mapping when necessary.
 *      - If the new parameter is an object, you need to do a bit more work. You need to map the parameter received from route
 *        to the object from the appStore, see "religion" for example in the "readSearchQueriesFromRoute" function.
 * 6. Edit the "generateQuery" function in SearchService.ts to handle the new search parameter, specially the specialCases,
 *      mapping, and/or tableItemToSearchFieldMap. More notes are in those files there.
 * After following these steps you should be able to use the new search parameter in the frontend.
 */

/**
 * Holds all the parameters that relate to paging and sorting.
 * Any fields here should be added to the getMetaUIKeys function below.
 */
export interface MetaSearchParams {
  order?: SearchOrder;
  offset?: string;
  page?: string;
  pageSize?: string;
}

/**
 * Holds all the search parameters that are shared across endpoints.
 */
interface BaseSearchParams extends MetaSearchParams {
  appointment?: string;
  appointment2?: string;
  autocomplete?: "true" | "false";
  caucus?: string;
  committee?: string;
  degree?: string;
  district?: string;
  ethnicity?: string;
  gender?: string;
  interest?: string;
  interest2?: string;
  jobtitle?: string;
  jobtitle2?: string;
  jobtitle2_option?: string;
  majmin?: string;
  office_location?: string;
  party?: string;
  q?: string;
  religion?: string;
  school?: string;
  state?: string;
  topagency?: string;
  topagency2?: string;
  type?: string;
}

/**
 * Holds all the search parameters for the Congressional endpoint.
 */
export interface CongressionalSearchParams extends BaseSearchParams {
  office?: string;
}

/**
 * Holds all the search parameters for the Federal endpoint.
 */
export interface FederalSearchParams extends BaseSearchParams {
  agency?: string;
}

/**
 * Holds all the search parameters for the State endpoint.
 */
export interface StateSearchParams extends BaseSearchParams {
  agency?: string;
  office?: string;
}

/**
 * Holds all the search parameters for the universal endpoint.
 */
export interface UniversalSearchParams extends BaseSearchParams {
  age?: string;
  agencies?: string;
  birth_year?: string;
  caucuses?: string;
  city?: string;
  committees?: string;
  country?: string;
  current_office_state_abbr?: string;
  current_office_congressional_district?: string;
  detail_level?: string;
  editorial_groups?: string;
  education?: string;
  hide_paywalled_profiles?: boolean;
  include_former?: boolean;
  industry?: string;
  key_policy_areas?: string;
  political_party?: string;
  profile_types?: string;
  sig_expression?: string;
  only_kw?: string;
}

/**
 * This interface is used to hold the parameters that only the UI uses to properly show results to the user.
 * Any fields here should be added to the getMetaUIKeys function below.
 */
export interface UIOnlyParams {
  view?: ProfileView;
}

/**
 * Helpers type for bundling all the search parameter keys.
 */
export type AllSearchParamsKeys =
  | keyof UniversalSearchParams
  | keyof StateSearchParams
  | keyof FederalSearchParams
  | keyof CongressionalSearchParams;

/**
 * Helper interface for bundling all the meta and ui search paramters.
 */
interface MetaUISearchParams extends MetaSearchParams, UIOnlyParams {}

/**
 * This wrapper around URLSearchParams is used to ensure type safety when handling the search parameters in the route.
 * This helps with building a query and reading params from the route.
 */
export class TypedURLSearchParams extends URLSearchParams {
  private params: URLSearchParams;

  constructor(params?: string) {
    super(params);
    this.params = new URLSearchParams(params);
  }

  append(key: AllSearchParamsKeys, value: string) {
    this.params.append(key, value);
  }

  /**
   * This is a helper function to get a UI parameter from the URL search params
   * Only use this to get UI parameters that are not part of the search query sent to the API
   * @param key - the key of the UI parameter
   * @returns the value of the UI parameter
   */
  getUIParam<K extends keyof UIOnlyParams>(key: K): UIOnlyParams[K] | null {
    return this.params.get(key) as UIOnlyParams[K] | null;
  }

  get(key: AllSearchParamsKeys): string | null {
    return this.params.get(key);
  }

  set(key: AllSearchParamsKeys, value: string): void {
    this.params.set(key, value);
  }

  delete(key: AllSearchParamsKeys): void {
    this.params.delete(key);
  }

  /**
   * This is a helper function to get a boolean value from the URL search params
   * @param key - the key of the boolean value
   * @returns {Boolean} the value of the boolean value
   */
  getBoolean(key: AllSearchParamsKeys): boolean {
    return this.params.get(key) === "true";
  }

  getAll(key: AllSearchParamsKeys): string[] {
    return this.params.getAll(key);
  }

  _createMetaUIKeys<T>(keys: (keyof T)[]): (keyof T)[] {
    return keys;
  }

  getMetaUIKeys(): Array<keyof MetaUISearchParams> {
    return this._createMetaUIKeys<MetaUISearchParams>([
      "order",
      "offset",
      "page",
      "pageSize",
      "view",
    ]);
  }

  /**
   * Checks if the params are empty while ignoring the meta keys
   * @returns {boolean} true if the params are empty, false otherwise
   */
  isEmpty(): boolean {
    const strippedParams = this.stripMetaKeys();
    return strippedParams.toString() === "";
  }

  /**
   * Strips the meta keys from the URLSearchParams object, mostly used for checking if actual user entered params are empty.
   * And to compare the params against previous searches.
   */
  stripMetaKeys(): TypedURLSearchParams {
    const metaKeys = this.getMetaUIKeys();
    const noMetaParams = new TypedURLSearchParams(this.toString());
    metaKeys.forEach((key) => noMetaParams.params.delete(key));
    return noMetaParams;
  }

  /**
   * Sorts the remaining keys alphabetically to provide very easy comparison of the params in string form
   */
  toString(): string {
    this.params.sort();
    return this.params.toString();
  }
}
